import { useCallback, useEffect, useState } from "react";

import { cellKey } from "./indexRosterDiffRatings";

/**
 * Cell ratings and comments for the differentiated grid. Rate for All only
 * fills students who already have that item (key present) and are in
 * editableStudentKeys. Clearing a rating also clears that cell's comment.
 * EXCLUDED cells are never bulk-cleared (rating or comment).
 */
const useDiffRatingState = (serverRatings, serverComments) => {
    const [ratings, setRatings] = useState(serverRatings || {});
    const [comments, setComments] = useState(serverComments || {});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        setRatings(serverRatings || {});
        setComments(serverComments || {});
        setHasChanges(false);
    }, [serverRatings, serverComments]);

    const setRating = useCallback((studentKey, grade, contentId, rating) => {
        const key = cellKey(studentKey, grade, contentId);
        const cleared = rating === null || rating === "";
        setRatings((prev) => ({ ...prev, [key]: rating }));
        if (cleared) {
            setComments((prev) => ({ ...prev, [key]: "" }));
        }
        setHasChanges(true);
    }, []);

    const setBulkRating = useCallback(
        (grade, contentId, rating, editableStudentKeys) => {
            const isClear = rating === null || rating === "";
            setRatings((prev) => {
                const next = { ...prev };
                (editableStudentKeys || []).forEach((studentKey) => {
                    const key = cellKey(studentKey, grade, contentId);
                    if (!(key in prev)) return;
                    const current = prev[key];
                    if (current === "EXCLUDED") return;
                    if (isClear) {
                        next[key] = "";
                    } else if (!current) {
                        next[key] = rating;
                    }
                });
                return next;
            });
            if (isClear) {
                setComments((prev) => {
                    const next = { ...prev };
                    (editableStudentKeys || []).forEach((studentKey) => {
                        const key = cellKey(studentKey, grade, contentId);
                        if (ratings[key] === "EXCLUDED") return;
                        if (key in next) next[key] = "";
                    });
                    return next;
                });
            }
            setHasChanges(true);
        },
        [ratings]
    );

    const setComment = useCallback((studentKey, grade, contentId, comment) => {
        setComments((prev) => ({
            ...prev,
            [cellKey(studentKey, grade, contentId)]: comment ?? ""
        }));
        setHasChanges(true);
    }, []);

    return { ratings, comments, hasChanges, setHasChanges, setRating, setBulkRating, setComment };
};

export default useDiffRatingState;
